import { useEffect, useState } from 'react';
import { useAdmin } from '../store/adminStore';
import { useOrderStore } from '../store/orderStore';
import { toast } from 'sonner';

function Analytics() {
  const { stats, revenue, payouts, fetchStats, fetchRevenue, fetchPayouts, updatePayoutStatus } =
    useAdmin();
  const { orders, fetchOrders } = useOrderStore();
  const [days, setDays] = useState(30);
  const [payoutId, setPayoutId] = useState<number | null>(null);

  useEffect(() => {
    Promise.all([
      fetchStats().catch((e: Error) => toast.error(e.message)),
      fetchRevenue(days).catch(() => null),
      fetchPayouts().catch(() => null),
      fetchOrders().catch(() => null),
    ]);
  }, [fetchStats, fetchRevenue, fetchPayouts, fetchOrders, days]);

  const changeDays = (d: number) => {
    setDays(d);
    fetchRevenue(d).catch((e: Error) => toast.error(e.message));
  };

  const handlePayoutStatus = async (id: number, status: string) => {
    setPayoutId(id);
    try {
      await updatePayoutStatus(id, status);
      toast.success('Payout status updated');
    } catch (e) {
      toast.error((e as Error).message);
    } finally {
      setPayoutId(null);
    }
  };

  const maxRevenue = Math.max(1, ...(revenue ?? []).map((r) => r.revenue));
  const statusCounts = orders.reduce<Record<string, number>>((acc, o) => {
    acc[o.status] = (acc[o.status] || 0) + 1;
    return acc;
  }, {});

  return (
    <div className="container">
      <h1 className="page-title">Analytics</h1>
      <p className="page-subtitle">Store performance, revenue trends, and payouts.</p>

      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-label">Total Revenue (incl. VAT)</div>
          <div className="stat-value success">€{(stats?.total_revenue ?? 0).toFixed(2)}</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Monthly Revenue (incl. VAT)</div>
          <div className="stat-value success">€{(stats?.monthly_revenue ?? 0).toFixed(2)}</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Orders</div>
          <div className="stat-value">{stats?.total_orders ?? 0}</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Pending Payouts</div>
          <div className="stat-value warning">{stats?.pending_payouts ?? 0}</div>
        </div>
      </div>

      <section className="section">
        <div className="panel">
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: '0.75rem',
            }}
          >
            <h2>Revenue Trend (incl. VAT)</h2>
            <div className="toolbar" style={{ margin: 0 }}>
              {[7, 30, 90].map((d) => (
                <button
                  key={d}
                  type="button"
                  className={`btn btn--sm ${days === d ? 'btn--primary' : 'btn--secondary'}`}
                  onClick={() => changeDays(d)}
                >
                  {d}d
                </button>
              ))}
            </div>
          </div>
          {(revenue ?? []).length === 0 ? (
            <p className="empty-state">No revenue data for this period.</p>
          ) : (
            <div className="revenue-chart">
              {(revenue ?? []).map((point) => (
                <div
                  key={point.date}
                  className="revenue-bar"
                  title={`${point.date}: €${point.revenue.toFixed(2)} (${point.orders} orders)`}
                  style={{ height: `${Math.max(4, (point.revenue / maxRevenue) * 100)}%` }}
                />
              ))}
            </div>
          )}
        </div>
      </section>

      <section className="section">
        <div className="panel">
          <h2>Order Status Breakdown</h2>
          {Object.keys(statusCounts).length === 0 ? (
            <p className="empty-state">No orders yet.</p>
          ) : (
            <div className="table-wrap">
              <table className="table">
                <thead>
                  <tr>
                    <th>Status</th>
                    <th>Count</th>
                  </tr>
                </thead>
                <tbody>
                  {Object.entries(statusCounts).map(([status, count]) => (
                    <tr key={status}>
                      <td>
                        <span className={`status-badge status--${status}`}>{status}</span>
                      </td>
                      <td>{count}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </section>

      <section className="section">
        <div className="panel">
          <h2>Payout Requests</h2>
          {(payouts ?? []).length === 0 ? (
            <p className="empty-state">No payout requests.</p>
          ) : (
            <div className="table-wrap">
              <table className="table">
                <thead>
                  <tr>
                    <th>Vendor</th>
                    <th>Amount</th>
                    <th>Method</th>
                    <th>Status</th>
                    <th>Note</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {payouts.map((p) => (
                    <tr key={p.id}>
                      <td>{p.vendor_name}</td>
                      <td>€{p.amount.toFixed(2)}</td>
                      <td>{p.method}</td>
                      <td>
                        <span className={`status-badge status--${p.status}`}>{p.status}</span>
                      </td>
                      <td>{p.note || '—'}</td>
                      <td>
                        <div className="actions">
                          {p.status === 'pending' && (
                            <>
                              <button
                                type="button"
                                className="btn btn--sm btn--primary"
                                disabled={payoutId === p.id}
                                onClick={() => handlePayoutStatus(p.id, 'processing')}
                              >
                                Process
                              </button>
                              <button
                                type="button"
                                className="btn btn--sm btn--danger"
                                disabled={payoutId === p.id}
                                onClick={() => handlePayoutStatus(p.id, 'failed')}
                              >
                                Fail
                              </button>
                            </>
                          )}
                          {(p.status === 'processing' || p.status === 'pending') && (
                            <button
                              type="button"
                              className="btn btn--sm btn--outline"
                              disabled={payoutId === p.id}
                              onClick={() => handlePayoutStatus(p.id, 'completed')}
                            >
                              Complete
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </section>
    </div>
  );
}

export default Analytics;
